import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  useCimPackage, useSendCim, useBuyerContacts, useAddBuyerContact, formatBytes,
  isConsentError, openConsentFlow,
} from "@/hooks/useCimPackage";

const toastSendError = (message: string) =>
  isConsentError(message)
    ? toast.error("Microsoft needs a one-time permission approval before the CRM can send mail with attachments.", {
        duration: 15000,
        action: { label: "Grant access", onClick: openConsentFlow },
      })
    : toast.error(message);

interface Props {
  open: boolean;
  onClose: () => void;
  clientId: string;
  linkId: string;
  buyerId: string;
  buyerName: string;
  /** "Buyer 2 of 3" when part of a multi-buyer send queue */
  stepLabel?: string;
  /** Buyer has no NDA milestone yet — Teaser + NDA backfill automatically on send */
  ndaMissing?: boolean;
  /** Called after a fully successful send (queue advances); falls back to onClose */
  onSent?: () => void;
  /** Open the package builder instead (no package configured yet) */
  onNeedsPackage: () => void;
}

export function SendCimDialog({ open, onClose, clientId, linkId, buyerId, buyerName, stepLabel, ndaMissing, onSent, onNeedsPackage }: Props) {
  const { data, isLoading } = useCimPackage(clientId, open);
  const { data: contacts = [] } = useBuyerContacts(open ? buyerId : null);
  const send = useSendCim(clientId);
  const addContact = useAddBuyerContact(buyerId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [touched, setTouched] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [cc, setCc] = useState("");
  const [ccSelectedIds, setCcSelectedIds] = useState<Set<string>>(new Set());
  const [ccFilter, setCcFilter] = useState("");
  const [failedIndexes, setFailedIndexes] = useState<number[] | null>(null);

  const emailContacts = contacts.filter((c) => c.email);

  // Reset per buyer — linkId changes as a multi-buyer queue steps forward.
  // MUST run before the preselect effect below: with cached contacts both fire
  // in the same commit on open, and reset-after-preselect would wipe the
  // primary selection with nothing to re-trigger it.
  useEffect(() => {
    setSelectedIds(new Set()); setTouched(false); setAddOpen(false);
    setNewName(""); setNewEmail(""); setCc(""); setFailedIndexes(null);
    setCcSelectedIds(new Set()); setCcFilter("");
  }, [open, linkId]);

  // Preselect the primary contact (fall back to the first with an email)
  useEffect(() => {
    if (!open || touched || selectedIds.size > 0) return;
    const primary = emailContacts.find((c) => c.isPrimary) ?? emailContacts[0];
    if (primary) setSelectedIds(new Set([primary.id]));
  }, [open, contacts, touched, selectedIds.size]);

  const pkg = data?.package ?? null;
  const docs = data?.docs ?? [];
  const docById = useMemo(() => new Map(docs.map((d) => [d.id, d])), [docs]);

  // No package yet → hand off to the builder
  useEffect(() => {
    if (open && data && !pkg) {
      toast.info("Set up the CIM package for this client first");
      onClose();
      onNeedsPackage();
    }
  }, [open, data, pkg, onClose, onNeedsPackage]);

  const recipients = emailContacts.filter((c) => selectedIds.has(c.id));
  const ccContacts = emailContacts.filter((c) => !selectedIds.has(c.id));
  const filteredCcContacts = ccFilter.trim()
    ? ccContacts.filter((c) => `${c.name} ${c.email ?? ""}`.toLowerCase().includes(ccFilter.trim().toLowerCase()))
    : ccContacts;
  const typedCc = cc.split(",").map((s) => s.trim()).filter((s) => s.includes("@"));
  const pickedCc = ccContacts.filter((c) => ccSelectedIds.has(c.id)).map((c) => c.email!);
  const seenCc = new Set<string>();
  const ccList = [...pickedCc, ...typedCc].filter((a) => {
    const k = a.toLowerCase();
    if (seenCc.has(k)) return false;
    seenCc.add(k);
    return true;
  });
  const canSend = !!pkg && recipients.length > 0 && !send.isPending;

  const toggleContact = (id: string) => {
    setTouched(true);
    setSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
    // A recipient can't also be a Cc — drop any stale Cc pick
    setCcSelectedIds((p) => { if (!p.has(id)) return p; const n = new Set(p); n.delete(id); return n; });
  };

  const toggleCc = (id: string) => {
    setCcSelectedIds((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const handleAddContact = () => {
    if (!newName.trim() || !newEmail.includes("@")) {
      toast.error("New contact needs a name and a valid email");
      return;
    }
    addContact.mutate(
      { name: newName.trim(), email: newEmail.trim() },
      {
        onSuccess: (c) => {
          setTouched(true);
          if (c?.id) setSelectedIds((p) => new Set(p).add(c.id));
          setNewName(""); setNewEmail(""); setAddOpen(false);
          toast.success(`${c?.name ?? "Contact"} added to ${buyerName} and selected`);
        },
        onError: (e) => toast.error(e.message),
      },
    );
  };

  const doSend = (emailIndexes?: number[]) => {
    send.mutate(
      {
        linkId,
        to: recipients.map((c) => ({ name: c.name, address: c.email! })),
        cc: ccList,
        ...(emailIndexes ? { emailIndexes, completesPackage: true } : {}),
        // Sending a CIM implies an executed NDA — backfill automatically
        ...(ndaMissing ? { alsoMarkMilestones: ["teaser", "nda"] as ("teaser" | "nda")[] } : {}),
      },
      {
        onSuccess: (res) => {
          const failed = res.results.filter((r) => !r.sent);
          if (failed.length === 0) {
            toast.success(`CIM package sent to ${recipients.map((r) => r.email).join(", ")}`);
            setFailedIndexes(null);
            (onSent ?? onClose)();
          } else {
            setFailedIndexes(failed.map((f) => f.index));
            toastSendError(`Email ${failed[0].index + 1} failed: ${failed[0].error ?? "unknown error"}`);
          }
        },
        onError: (e) => toastSendError(e.message),
      },
    );
  };

  if (!pkg) {
    return (
      <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Send CIM — {buyerName}</DialogTitle></DialogHeader>
          <div className="p-4 text-center text-sm text-slate-400">{isLoading ? "Loading…" : "No package configured."}</div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            Send CIM — {buyerName}
            {stepLabel && <span className="ml-2 text-sm font-normal text-slate-400">{stepLabel}</span>}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Send to</div>
            <div className="max-h-40 space-y-0.5 overflow-y-auto rounded-md border border-slate-200 p-1.5 dark:border-slate-900">
              {emailContacts.map((c) => (
                <label key={c.id} className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${failedIndexes ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${selectedIds.has(c.id) ? "bg-blue-50 dark:bg-blue-950/40" : failedIndexes ? "" : "hover:bg-slate-50 dark:bg-slate-950/40"}`}>
                  <input type="checkbox" disabled={failedIndexes != null} checked={selectedIds.has(c.id)} onChange={() => toggleContact(c.id)} />
                  <span className="font-medium text-slate-900 dark:text-slate-200">{c.name}</span>
                  {c.isPrimary && <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-900/30">primary</span>}
                  <span className="ml-auto truncate text-xs text-slate-400">{c.email}</span>
                </label>
              ))}
              {emailContacts.length === 0 && (
                <div className="px-1.5 py-2 text-xs text-amber-600 dark:text-amber-400">No contacts with an email on {buyerName} — add one below.</div>
              )}
              {!addOpen ? (
                <button className="px-1.5 py-1 text-xs font-semibold text-blue-600 hover:underline disabled:opacity-50 disabled:no-underline dark:text-blue-400" disabled={failedIndexes != null} onClick={() => setAddOpen(true)}>
                  + Add a contact to {buyerName}
                </button>
              ) : (
                <div className="flex items-center gap-2 px-1.5 py-1">
                  <Input className="h-7 flex-1 text-sm" placeholder="Full name" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
                  <Input className="h-7 flex-[1.5] text-sm" placeholder="email@buyer.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
                  <Button size="sm" className="h-7 px-2 text-xs" disabled={addContact.isPending} onClick={handleAddContact}>
                    {addContact.isPending ? "Adding…" : "Add"}
                  </Button>
                </div>
              )}
            </div>
            {recipients.length > 1 && (
              <div className="mt-1 text-[11px] text-slate-400">
                The greeting's {"{{first_name}}"} uses the first selected contact ({recipients[0].name.split(" ")[0]}).
              </div>
            )}
          </div>

          <div>
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-400">Cc (optional)</div>
            {ccContacts.length > 0 && (
              <div className="mb-1.5 rounded-md border border-slate-200 p-1.5 dark:border-slate-900">
                {ccContacts.length > 5 && (
                  <Input
                    className="mb-1 h-7 text-xs"
                    placeholder={`Search contacts at ${buyerName}…`}
                    disabled={failedIndexes != null}
                    value={ccFilter}
                    onChange={(e) => setCcFilter(e.target.value)}
                  />
                )}
                <div className="max-h-32 space-y-0.5 overflow-y-auto">
                  {filteredCcContacts.map((c) => (
                    <label key={c.id} className={`flex items-center gap-2 rounded px-1.5 py-1 text-sm ${failedIndexes ? "cursor-not-allowed opacity-60" : "cursor-pointer"} ${ccSelectedIds.has(c.id) ? "bg-blue-50 dark:bg-blue-950/40" : failedIndexes ? "" : "hover:bg-slate-50 dark:bg-slate-950/40"}`}>
                      <input type="checkbox" disabled={failedIndexes != null} checked={ccSelectedIds.has(c.id)} onChange={() => toggleCc(c.id)} />
                      <span className="font-medium text-slate-900 dark:text-slate-200">{c.name}</span>
                      {c.isPrimary && <span className="rounded bg-slate-100 px-1 text-[10px] font-semibold text-slate-500 dark:bg-slate-900/30">primary</span>}
                      <span className="ml-auto truncate text-xs text-slate-400">{c.email}</span>
                    </label>
                  ))}
                  {filteredCcContacts.length === 0 && (
                    <div className="px-1.5 py-1 text-xs text-slate-400">No matching contacts.</div>
                  )}
                </div>
              </div>
            )}
            <Input className="h-8" placeholder="Or paste emails — colleague@firm.com, other@firm.com" disabled={failedIndexes != null} value={cc} onChange={(e) => setCc(e.target.value)} />
            {failedIndexes != null && (
              <div className="mt-1 text-[11px] text-slate-400">Recipients are locked during a retry so the package stays consistent.</div>
            )}
          </div>

          {pkg.emails.map((email, i) => {
            const size = email.documentIds.reduce((s, id) => s + (docById.get(id)?.fileSize ?? 0), 0);
            const failed = failedIndexes?.includes(i);
            const alreadySent = failedIndexes != null && !failedIndexes.includes(i);
            return (
              <div key={i} className={`rounded-lg border p-3 ${failed ? "border-red-200 bg-red-50/50 dark:border-red-900 dark:bg-red-950/40" : alreadySent ? "border-green-200 bg-green-50/40 dark:border-green-900 dark:bg-green-950/40" : "border-slate-200 dark:border-slate-900"}`}>
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-700 dark:text-slate-400">
                    Email {i + 1}
                    {alreadySent && <span className="ml-2 font-semibold text-green-700 dark:text-green-400">sent ✓</span>}
                    {failed && <span className="ml-2 font-semibold text-red-600 dark:text-red-400">failed — will retry</span>}
                  </span>
                  <span className="tabular-nums text-slate-400">{formatBytes(size)}</span>
                </div>
                <div className="mt-1 truncate text-sm font-medium text-slate-900 dark:text-slate-200">{email.subject}</div>
                {(email.cc?.length ?? 0) > 0 && (
                  <div className="mt-0.5 text-[11px] text-slate-500">Cc: {email.cc!.join(", ")}</div>
                )}
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {email.documentIds.map((id) => {
                    const d = docById.get(id);
                    return (
                      <span key={id} className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-600 dark:bg-slate-900/30 dark:text-slate-400">
                        {d?.fileName ?? "missing document"}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {ndaMissing && (
            <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
              This buyer's Teaser and NDA milestones will be marked as of today — sending a CIM implies an executed NDA.
            </div>
          )}

          <div className="text-[11px] text-slate-400">
            Sends from your Outlook mailbox with your signature and the firm disclaimer appended, and lands in your Sent Items. compliance@firstturncapital.com is bcc'd on every email. Marks the CIM milestone and logs the activity when everything goes out.
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button size="sm" disabled={!canSend} onClick={() => doSend(failedIndexes ?? undefined)}>
            {send.isPending
              ? "Sending…"
              : failedIndexes
                ? `Retry email ${failedIndexes.map((i) => i + 1).join(", ")}`
                : `Send${recipients.length > 1 ? ` to ${recipients.length} contacts` : ""}${pkg.emails.length > 1 ? ` (${pkg.emails.length} emails)` : ""}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
